import { OrgMemberRole } from "@garther/interfaces";
import { Button, Popup, Select } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { useEffect, useState } from "react";

import { useUpdateOrgMemberRole } from "@/features/org/hooks/use-org-query";

import { RoleOptions } from "./constants";

interface EditTarget {
  userId: string;
  name: string;
  role: OrgMemberRole;
}

interface OrgMemberEditModalProps {
  orgId: string;
  member: EditTarget | null;
  onClose: () => void;
}

export function OrgMemberEditModal({ orgId, member, onClose }: OrgMemberEditModalProps) {
  const { t } = useI18n();

  const [editRole, setEditRole] = useState<OrgMemberRole>(OrgMemberRole.MEMBER);

  useEffect(() => {
    if (member) setEditRole(member.role);
  }, [member]);

  const { mutate: updateRole, isPending: isUpdating } = useUpdateOrgMemberRole({
    onSuccess: onClose
  });

  const handleUpdateRole = () => {
    if (!member || isUpdating) return;
    updateRole({ orgId, userId: member.userId, role: editRole });
  };

  return (
    <Popup isOpen={!!member} onClose={onClose}>
      <div className="space-y-6">
        <div>
          <h2 className="text-responsive-lg font-semibold text-text-primary">
            {t("org.members.editModal.title")}
          </h2>
          <p className="text-responsive-base text-text-secondary mt-1">
            {t("org.members.editModal.description", {
              name: member?.name ?? ""
            })}
          </p>
        </div>

        <Select
          label={t("org.members.modal.roleLabel")}
          options={RoleOptions}
          value={editRole}
          onChange={(e) => setEditRole(e.target.value as OrgMemberRole)}
          required
        />

        <div className="flex justify-end space-x-3">
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleUpdateRole} disabled={isUpdating || editRole === member?.role}>
            {isUpdating ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </div>
    </Popup>
  );
}
